import { useCallback, useEffect, useState, useRef } from "react";
import { observer } from "mobx-react-lite";
import { ChevronDown, ChevronRight } from "lucide-react";

import { useTran } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import { useOrganizationStore } from "../hooks";
import { getUnitTypeMeta } from "../utils/organization.helpers";

const INDENT_SIZE = 24;

const getCollapseKey = (node) => {
  if (!node) return null;
  const baseId = node.id || node._id || node.key || node.name;
  if (!baseId) return null;
  if (node.groupKey) {
    return `${node.groupKey}:${baseId}`;
  }
  return baseId;
};

const EmptyState = () => {
  const { t } = useTran();
  return (
    <div className="flex h-full flex-col items-center justify-center rounded-radius border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
      <p className="font-medium">{t("No units configured yet.")}</p>
      <p className="mt-1 text-xs">
        {t("Add your parent company and organization levels to get started.")}
      </p>
    </div>
  );
};

const TreeNode = ({
  node,
  depth,
  selectedId,
  onSelect,
  collapsedNodes,
  onToggleCollapse,
}) => {
  const { t } = useTran();
  const meta = getUnitTypeMeta(node.unitLevel);
  const Icon = meta.icon;
  const isActive = node.id === selectedId;
  const isGroup = Boolean(node.isGroup);
  const indentation = depth * INDENT_SIZE;
  const hasChildren = node.children?.length > 0;
  const collapseKey = getCollapseKey(node);
  const isCollapsed = collapseKey ? collapsedNodes?.has(collapseKey) : false;
  const locationLabel =
    node.locationLabel || node.countryLabel || node.country || node.regionLabel;

  const handleToggleCollapse = (event) => {
    event.stopPropagation();
    if (!collapseKey) return;
    onToggleCollapse?.(collapseKey);
  };

  const handleSelect = () => {
    if (isGroup) return;
    onSelect?.(node.id);
  };

  return (
    <div className="flex flex-col">
      <button
        type="button"
        onClick={handleSelect}
        aria-disabled={isGroup}
        className={`relative flex w-full flex-col gap-1 rounded-radius border border-transparent px-3 py-2 text-left transition ${
          isActive ? "bg-primary/10 text-primary" : "hover:bg-muted/60"
        }`}
        style={{ paddingLeft: indentation }}
      >
        <div className="flex items-center gap-2">
          {hasChildren ? (
            <span
              role="button"
              aria-label={isCollapsed ? "Expand unit" : "Collapse unit"}
              tabIndex={-1}
              onClick={handleToggleCollapse}
              className="flex h-5 w-5 items-center justify-center rounded border border-border bg-background text-muted-foreground transition hover:bg-muted ml-2"
            >
              {isCollapsed ? (
                <ChevronRight className="h-3 w-3" />
              ) : (
                <ChevronDown className="h-3 w-3" />
              )}
            </span>
          ) : (
            <span className="h-5 w-5" />
          )}
          <span
            className={`flex h-8 w-8 items-center justify-center rounded-full bg-background shadow-sm ${meta.color}`}
          >
            <Icon className="h-4 w-4" />
          </span>
          <div className="flex flex-1 flex-col">
            <span className="text-sm font-semibold text-foreground">
              {node.name}
            </span>
            <span className={`text-xs ${meta.color}`}>{t(meta.label)}</span>
          </div>
          {locationLabel ? (
            <Badge
              variant="outline"
              className="border-primary/30 bg-background text-[11px] text-primary"
            >
              {locationLabel}
            </Badge>
          ) : null}
        </div>
      </button>

      {hasChildren && !isCollapsed
        ? (node.children || []).map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              collapsedNodes={collapsedNodes}
              onToggleCollapse={onToggleCollapse}
            />
          ))
        : null}
    </div>
  );
};

const OrganizationTree = observer(({ tree: treeOverride, headerActions }) => {
  const { t } = useTran();
  const {
    tree: storeTree,
    selectedId,
    selectUnit,
    loading,
  } = useOrganizationStore();
  const tree = treeOverride ?? storeTree;
  const [collapsedNodes, setCollapsedNodes] = useState(() => new Set());

  const knownNodeIdsRef = useRef(new Set());

  useEffect(() => {
    if (!Array.isArray(tree)) return;

    const defaultCollapsedTypes = ["level_1", "business_type", "country"];

    const newlySeenIds = [];
    const walk = (nodes = []) => {
      nodes.forEach((node) => {
        const id = getCollapseKey(node);
        if (!id) return;

        if (!knownNodeIdsRef.current.has(id)) {
          knownNodeIdsRef.current.add(id);
          newlySeenIds.push(id);
        }

        if (Array.isArray(node.children)) walk(node.children);
      });
    };

    walk(tree);

    if (newlySeenIds.length === 0) return;

    setCollapsedNodes((prev) => {
      const next = new Set(prev);

      const markNewDefaults = (nodes = []) => {
        nodes.forEach((node) => {
          const id = getCollapseKey(node);
          if (!id) return;

          const isNew = newlySeenIds.includes(id);
          const shouldDefaultCollapse =
            isNew &&
            defaultCollapsedTypes.includes(node.unitLevel) &&
            node.children?.length;

          if (shouldDefaultCollapse) next.add(id);

          if (Array.isArray(node.children)) markNewDefaults(node.children);
        });
      };

      markNewDefaults(tree);
      return next;
    });
  }, [tree]);

  const handleToggleCollapse = useCallback((nodeId) => {
    setCollapsedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  return (
    <div className="flex h-full w-full flex-col rounded-radius border border-border bg-card shadow-sm">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
          <p className="text-base font-semibold text-foreground">
            {t("Organization Tree")}
          </p>
          <p className="text-xs text-muted-foreground">
            {tree?.length
              ? t("Select a unit to view its details.")
              : t("Awaiting initial setup.")}
          </p>
        </div>
        {headerActions ? (
          <div className="flex items-center">{headerActions}</div>
        ) : null}
      </div>

      <div className="flex-1 overflow-y-auto p-3">
        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div
                key={`skeleton-${index}`}
                className="animate-pulse rounded-radius border border-border bg-muted/40 p-4"
              >
                <div className="h-3 w-1/2 rounded-full bg-muted" />
                <div className="mt-2 h-2 w-1/4 rounded-full bg-muted/70" />
              </div>
            ))}
          </div>
        ) : null}

        {!loading && (!tree || tree.length === 0) ? <EmptyState /> : null}

        {!loading && tree?.length
          ? tree.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                depth={0}
                selectedId={selectedId}
                onSelect={selectUnit}
                collapsedNodes={collapsedNodes}
                onToggleCollapse={handleToggleCollapse}
              />
            ))
          : null}
      </div>
    </div>
  );
});

export default OrganizationTree;
